import React, { useState, useEffect, useCallback } from 'react';
import '../css/popup.css';
import { API_URL } from '../helpers/api';
import TruncatableHtmlContent from '../utils/TruncatableHtmlContent';

const PopupManager = ({ fetchData }) => {
    const [popups, setPopups] = useState([]);
    const [activePopup, setActivePopup] = useState(null);

    // Fetch eligible popups
    const fetchEligiblePopups = useCallback(async () => {
        try {
            // Fetch all popups using your fetchData function
            const allPopups = await fetchData('/api/popups?populate=*');
            // console.log(allPopups);

            // Filter popups based on active status, date, and display eligibility
            const now = new Date();
            const eligiblePopups = allPopups.filter(popup =>
                popup.active &&
                new Date(popup.startDate) <= now &&
                new Date(popup.endDate) >= now &&
                isPopupEligible(popup)
            ).sort((a, b) => (b.priority || 0) - (a.priority || 0));

            // Set the filtered popups
            setPopups(eligiblePopups);

            // Select and display a popup if available
            if (eligiblePopups.length > 0) {
                selectPopup(eligiblePopups);
            }
        } catch (error) {
            console.error('Error fetching popups:', error);
        }
    }, [fetchData]);

    // Check popup display eligibility
    const isPopupEligible = useCallback((popup) => {
        // First, check if the popup is active
        if (!popup.active) return false;

        // Check target audience
        const isNewVisitor = !localStorage.getItem('visited');
        const isReturningVisitor = localStorage.getItem('visited');

        const audienceMatch = popup.targetAudience.includes('all') ||
            (isNewVisitor && popup.targetAudience.includes('new')) ||
            (isReturningVisitor && popup.targetAudience.includes('returning'));

        // Check display frequency
        const popupKey = `popup_${popup.documentId}`;
        const displayCount = parseInt(localStorage.getItem(popupKey) || '0');

        let frequencyCheck = false;
        switch (popup.displayFrequency) {
            case 'always':
                frequencyCheck = true;
                break;
            case 'session':
                frequencyCheck = !sessionStorage.getItem(popupKey);
                break;
            case 'once':
                frequencyCheck = displayCount === 0;
                break;
            default:
                frequencyCheck = false;
        }

        return audienceMatch && frequencyCheck;
    }, []);

    // Select and display popup
    const selectPopup = useCallback((eligiblePopups) => {
        // Mark site as visited
        if (!localStorage.getItem('visited')) {
            localStorage.setItem('visited', 'true');
        }

        // Select first eligible popup
        const selectedPopup = eligiblePopups[0];

        if (selectedPopup) {
            setActivePopup(selectedPopup);

            // Update display tracking
            const popupKey = `popup_${selectedPopup.documentId}`;
            const currentCount = parseInt(localStorage.getItem(popupKey) || '0');
            localStorage.setItem(popupKey, (currentCount + 1).toString());

            if (selectedPopup.displayFrequency === 'session') {
                sessionStorage.setItem(popupKey, 'true');
            }
        }
    }, []);

    // Close popup and prevent re-showing
    const handleClosePopup = () => {
        setActivePopup(null);
    };

    // Fetch and process popups on component mount
    useEffect(() => {
        fetchEligiblePopups();
    }, [fetchEligiblePopups]);

    // Render popup if active
    if (!activePopup) return null;

    return (
        <div
            className="custom-modal modal fade show bd-example-modal-lg"
            tabIndex="-1"
            style={{
                display: 'block',
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'black'
            }}
        >
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content text-center">
                    {/* Header with Title */}
                    <div className="modal-header">
                        {/* <h5 className="modal-title">{activePopup.title}</h5> */}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={handleClosePopup}
                        ></button>
                    </div>

                    {/* Main Image */}
                    {activePopup.mainImage && (
                        <div className="modal-body p-0">
                            <img 
                                src={API_URL + activePopup.mainImage.url} 
                                alt={activePopup?.title} 
                                className=""
                                width="350"
                            />
                            {activePopup.mainImage.caption && (
                                <p className="text-center mt-2 text-muted">
                                    {activePopup.mainImage.caption}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Content */}
                    <div className="modal-body">

                        {/* Render rich text content */}
                        <TruncatableHtmlContent
                            html={activePopup?.content}
                        />
                    </div>

                    {/* Footer with Optional Link */}
                    {activePopup.link && activePopup.link.url && (
                        <div className="modal-footer">
                            <a
                                href={activePopup.link.url}
                                className="btn btn-warning rounded-pill fw-semibold px-4"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {activePopup.link.text || 'En savoir plus'}
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PopupManager;